import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { parse, serialize } from 'tinyduration';
import { removeTimeZone } from '../hooks/useDateTimeFormat';
import { isString } from './guards';

type OneToNine = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type ZeroToNine = 0 | OneToNine;
type Days = `${0}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}` | `${1 | 2}${ZeroToNine}` | `3${0 | 1}`;
type Month = `0${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}` | `1${0 | 1 | 2}`;
type Year = `${19 | 20}${ZeroToNine}${ZeroToNine}`;
export type IsoDateType = `${Year}-${Month}-${Days}` | '';

const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const ISO_DATE_TIME_FORMAT = `${ISO_DATE_FORMAT}'T'HH:mm:ss.SSS`;
const SHORT_ISO_TIME_FORMAT = `HH:mm`;
const ISO_DATE_TIME_FORMAT_WITH_TIMEZONE = `${ISO_DATE_FORMAT}'T'HH:mm:ss.SSSxxx`;

export const duration = { parse, serialize };

export const dateToIsoDateFormat = (date?: Date | null): IsoDateType =>
  isValidDate(date) ? (format(date, ISO_DATE_FORMAT) as IsoDateType) : '';

export const dateToIsoDateTimeFormat = (date?: Date | null): string =>
  isValidDate(date) ? format(date, ISO_DATE_TIME_FORMAT) : '';

export const dateToShortIsoTimeFormat = (date?: Date | null): string =>
  isValidDate(date) ? format(date, SHORT_ISO_TIME_FORMAT) : '';

export const dateToIsoDateTimeFormatWithTimeZone = (date?: Date | null): string =>
  isValidDate(date) ? format(date, ISO_DATE_TIME_FORMAT_WITH_TIMEZONE) : '';

export const fullNumericDateTimeString = (date: string | undefined) =>
  format(utcToZonedTime(date ?? new Date(), 'UTC'), 'yyyyMMddHHmmss');

type SimpleLocalDate = `${number}-${number}-${number}`;

/**
 * Checks if a string is a valid ISO date (yyyy-MM-dd).
 */
export function isLocalDate(v: any): v is SimpleLocalDate {
  if (!isString(v)) {
    return false;
  }
  return new RegExp('^\\d{4}-\\d{2}-\\d{2}$').test(v) && isValidDate(new Date(v));
}

export function isValidTime(v: any): v is `${number}:${Minutes}` {
  if (!isString(v)) {
    return false;
  }
  return new RegExp('^\\d{2}:\\d{2}$').test(v) && isValidDate(new Date(`2000-01-01T${v}`));
}

/**
 * Checks if a string is a valid ISO date time  (yyyy-MM-dd'T'HH:mm:ss.SSS).
 */
export function isLocalDateTime(v: any): v is SimpleLocalDate {
  if (!isString(v)) {
    return false;
  }
  return new RegExp('^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}.\\d{3}$').test(v) && isValidDate(new Date(v));
}

/**
 * Use to convert an ISO simple date to a local date which will
 * be displayed as is in the user's browser without any time
 * zone adjustment.
 */
export const isoDateToLocalDate = (date?: string | Date): Date | null => {
  if (date instanceof Date) {
    return isValidDate(date) ? removeTimeZone(date) : null;
  }

  return date ? parseISO(date) : null;
};

export const isValidDate = (d?: Date | null): d is Date => {
  return d instanceof Date && !Number.isNaN(d.getTime());
};

type Minutes = `${0 | 1 | 2 | 3 | 4 | 5}${0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;
type Seconds = Minutes;
type TimeString =
  | `${number}:${Minutes}`
  | `${number}:${Minutes}:${Seconds}`
  | `${number}h ${Minutes}m`
  | `${number}h ${Minutes}m ${Seconds}s`;
type TimeUnit = 'second' | 'minute' | 'hour' | 'millisecond';

export interface ToTimeStringOptions {
  inputUnit?: TimeUnit;
  format?: '00:00' | '00:00:00' | '0h 0m' | '0h 0m 0s';
}

const MILLISECONDS_MAP = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  millisecond: 1
} as const;

export function convertTimeUnit(amount: number, from: TimeUnit, to: TimeUnit): number {
  return (amount * MILLISECONDS_MAP[from]) / MILLISECONDS_MAP[to];
}

function splitTime(
  amount: number,
  from: TimeUnit
): { hours: number; minutes: number; seconds: number; milliseconds: number } {
  const milliseconds = convertTimeUnit(amount, from, 'millisecond');
  const hours = Math.floor(milliseconds / MILLISECONDS_MAP.hour);
  const minutes = Math.floor((milliseconds % MILLISECONDS_MAP.hour) / MILLISECONDS_MAP.minute);
  const seconds = Math.floor((milliseconds % MILLISECONDS_MAP.minute) / MILLISECONDS_MAP.second);
  const remainingMilliseconds = milliseconds % MILLISECONDS_MAP.second;

  return { hours, minutes, seconds, milliseconds: remainingMilliseconds };
}

export const toTimeString = (amount: number, options?: ToTimeStringOptions): TimeString => {
  const prefix = amount < 0 ? '-' : '';
  const absoluteAmount = Math.abs(amount);
  const { inputUnit = 'second', format = '0h 0m' } = options ?? {};

  const { hours, minutes, seconds } = splitTime(absoluteAmount, inputUnit);

  if (format === '0h 0m') {
    return `${prefix}${hours}h ${minutes + Math.round(seconds / 60)}m` as TimeString;
  }
  if (format === '0h 0m 0s') {
    return `${prefix}${hours}h ${minutes}m ${seconds}s` as TimeString;
  }
  if (format === '00:00') {
    return `${prefix}${hours.toString().padStart(2, '0')}:${(minutes + Math.round(seconds / 60)).toString().padStart(2, '0')}` as TimeString;
  }
  return `${prefix}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}` as TimeString;
};

export const getOrdinalDay = (dayOfMonth: number, month?: number): string =>
  // 01/01/2021 has 31 days so we can be sure that output number will be valid one
  format(new Date(Date.UTC(2022, month ?? 0, dayOfMonth)), 'do');

// Required by yearlyScheduleModelHelper
export const getMonthAndOrdinalDay = (month: number, firstMonthEquals1 = false, day?: number): string =>
  format(new Date(Date.UTC(new Date().getFullYear(), month - (firstMonthEquals1 ? 1 : 0), day)), 'MMMM do');

export const sortDates = (date1: string | Date | null, date2: string | Date | null, fromOldest = true) => {
  const d1 = (date1 ? new Date(date1) : new Date()).getTime();
  const d2 = (date2 ? new Date(date2) : new Date()).getTime();
  return fromOldest ? d1 - d2 : d2 - d1;
};
